// web-frontend/src/tutorial/steps.js
// Step definitions per user_type. Each step is a plain object consumed by
// TutorialOverlay. The runtime evaluates step.condition({ user, isMobile,
// isSidebarOpen, location }) to decide whether to render or skip this step.
// If condition returns false, the runtime auto-advances without showing the
// step.
//
// Advance types:
//   { type: 'next_button' }                                   -- user clicks Next
//   { type: 'route', path: '/matching' }                      -- url path matches
//   { type: 'dom_added', selector: '[data-tutorial="x"]' }    -- selector appears in DOM
//   { type: 'class_added', target: '.sidebar', className: 'mobile-open' }
//   { type: 'click_target' }                                  -- click on the spotlight target

// =========================================================================
// Always-first step: language picker.
// Lets the user pick ko / en / ja / zh before any other content. Choice is
// persisted (DB user.preferred_language + appStore + i18nService) so the rest
// of the tutorial AND every page render in the same language. Solves the
// "tutorial in Korean but page in English" drift on first login.
// =========================================================================
const LANGUAGE_PICKER_STEP = {
  id: 'language-picker',
  type: 'language_picker',
  // titleKey is only used by the StepsPanel checklist; the picker itself has
  // its own multilingual rendering inside TutorialOverlay. No advance config —
  // TutorialOverlay calls advance() once a language is chosen.
  titleKey: 'picker.step_title',
  ensureRoute: '/',
};

// =========================================================================
// Shared sub-step: open the mobile sidebar.
// Shown only on mobile/tablet when the sidebar is closed.
// =========================================================================
const SHARED_OPEN_MENU = {
  id: 'shared-open-menu',
  type: 'spotlight',
  titleKey: 'shared.open_menu.title',
  bodyKey: 'shared.open_menu.body',
  target: '[data-tutorial="hamburger-menu"]',
  placement: 'bottom',
  spotlightPadding: 6,
  condition: ({ isMobile, isSidebarOpen }) => isMobile && !isSidebarOpen,
  advance: { type: 'class_added', target: '.sidebar', className: 'mobile-open' },
};

// =========================================================================
// Comprehensive tour for matching-eligible verified users
// (COMPANY / BUYER verified / BUSINESS_VISITOR verified)
// Order: welcome -> sidebar walkthrough -> matching deep-dive -> done.
// We tour the sidebar items first because they share the same surface (and on
// mobile the sidebar can stay open between them). The matching segment is
// last because clicking "Matching" auto-closes the mobile sidebar.
// =========================================================================
function buildMatchingTour(prefix) {
  return [
    LANGUAGE_PICKER_STEP,
    {
      id: prefix + '-welcome',
      type: 'centered',
      titleKey: prefix + '.welcome.title',
      bodyKey: prefix + '.welcome.body',
      advance: { type: 'next_button' },
      ensureRoute: '/',
    },
    SHARED_OPEN_MENU,
    {
      id: prefix + '-companies',
      type: 'spotlight',
      titleKey: prefix + '.companies.title',
      bodyKey: prefix + '.companies.body',
      target: '[data-tutorial="nav-companies"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-events',
      type: 'spotlight',
      titleKey: prefix + '.events.title',
      bodyKey: prefix + '.events.body',
      target: '[data-tutorial="nav-events"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-chats',
      type: 'spotlight',
      titleKey: prefix + '.chats.title',
      bodyKey: prefix + '.chats.body',
      target: '[data-tutorial="nav-chats"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-profile',
      type: 'spotlight',
      titleKey: prefix + '.profile.title',
      bodyKey: prefix + '.profile.body',
      target: '[data-tutorial="nav-profile"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-carbon',
      type: 'spotlight',
      titleKey: prefix + '.carbon.title',
      bodyKey: prefix + '.carbon.body',
      target: '[data-tutorial="carbon-widget"]',
      placement: 'top',
      spotlightPadding: 6,
      // 🔥 [2026-06-30] 탄소 위젯이 없는 테넌트/화면에선 이 단계를 건너뜀(빈 스포트라이트 stall 방지)
      condition: () => typeof document !== 'undefined' && !!document.querySelector('[data-tutorial="carbon-widget"]'),
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-matching',
      type: 'spotlight',
      titleKey: prefix + '.matching.title',
      bodyKey: prefix + '.matching.body',
      target: '[data-tutorial="nav-matching"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'route', path: '/matching' },
    },
    {
      id: prefix + '-aiBtn',
      type: 'spotlight',
      titleKey: prefix + '.aiBtn.title',
      bodyKey: prefix + '.aiBtn.body',
      target: '[data-tutorial="ai-analysis-btn"]',
      placement: 'bottom',
      spotlightPadding: 8,
      advance: { type: 'click_target' },
      ensureRoute: '/matching',
    },
    {
      id: prefix + '-analyzing',
      type: 'spotlight',
      titleKey: prefix + '.analyzing.title',
      bodyKey: prefix + '.analyzing.body',
      target: '.calculating-overlay',
      placement: 'top',
      spotlightPadding: 12,
      // Skip the wait step if the analyzed badge is already on screen
      // (e.g. cached results shown immediately).
      condition: () => !document.querySelector('[data-tutorial="analyzed-badge"]'),
      advance: { type: 'dom_added', selector: '[data-tutorial="analyzed-badge"]' },
      // 🔥 [2026-07-13] 90초 → 15초: 추천 0건 등으로 배지가 영영 안 뜨는 케이스의 최악 대기 단축
      //   (주 방어는 TutorialOverlay useTargetRect 의 타깃 소멸 재무장 → centered Next 폴백)
      timeout: 15000,
    },
    {
      id: prefix + '-analyzed',
      type: 'spotlight',
      titleKey: prefix + '.analyzed.title',
      bodyKey: prefix + '.analyzed.body',
      target: '[data-tutorial="analyzed-badge"]',
      placement: 'bottom',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-done',
      type: 'centered',
      titleKey: prefix + '.done.title',
      bodyKey: prefix + '.done.body',
      advance: { type: 'next_button' },
    },
  ];
}

// =========================================================================
// Pending approval tour (BUYER / BUSINESS_VISITOR not yet verified).
// =========================================================================
function buildPendingTour(prefix) {
  return [
    LANGUAGE_PICKER_STEP,
    {
      id: prefix + '-welcome',
      type: 'centered',
      titleKey: prefix + '.welcome.title',
      bodyKey: prefix + '.welcome.body',
      advance: { type: 'next_button' },
      ensureRoute: '/',
    },
    SHARED_OPEN_MENU,
    {
      id: prefix + '-companies',
      type: 'spotlight',
      titleKey: prefix + '.companies.title',
      bodyKey: prefix + '.companies.body',
      target: '[data-tutorial="nav-companies"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-events',
      type: 'spotlight',
      titleKey: prefix + '.events.title',
      bodyKey: prefix + '.events.body',
      target: '[data-tutorial="nav-events"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-profile',
      type: 'spotlight',
      titleKey: prefix + '.profile.title',
      bodyKey: prefix + '.profile.body',
      target: '[data-tutorial="nav-profile"]',
      placement: 'right',
      spotlightPadding: 6,
      advance: { type: 'next_button' },
    },
    {
      id: prefix + '-done',
      type: 'centered',
      titleKey: prefix + '.done.title',
      bodyKey: prefix + '.done.body',
      advance: { type: 'next_button' },
    },
  ];
}

// =========================================================================
// INVESTOR — no matching access. Browse + bookmark + chats + events + profile.
// =========================================================================
const INVESTOR_STEPS = [
  LANGUAGE_PICKER_STEP,
  {
    id: 'investor-welcome',
    type: 'centered',
    titleKey: 'investor.welcome.title',
    bodyKey: 'investor.welcome.body',
    advance: { type: 'next_button' },
    ensureRoute: '/',
  },
  SHARED_OPEN_MENU,
  {
    id: 'investor-companies',
    type: 'spotlight',
    titleKey: 'investor.companies.title',
    bodyKey: 'investor.companies.body',
    target: '[data-tutorial="nav-companies"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'investor-bookmark',
    type: 'centered',
    titleKey: 'investor.bookmark.title',
    bodyKey: 'investor.bookmark.body',
    advance: { type: 'next_button' },
  },
  {
    id: 'investor-events',
    type: 'spotlight',
    titleKey: 'investor.events.title',
    bodyKey: 'investor.events.body',
    target: '[data-tutorial="nav-events"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'investor-chats',
    type: 'spotlight',
    titleKey: 'investor.chats.title',
    bodyKey: 'investor.chats.body',
    target: '[data-tutorial="nav-chats"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'investor-profile',
    type: 'spotlight',
    titleKey: 'investor.profile.title',
    bodyKey: 'investor.profile.body',
    target: '[data-tutorial="nav-profile"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'investor-done',
    type: 'centered',
    titleKey: 'investor.done.title',
    bodyKey: 'investor.done.body',
    advance: { type: 'next_button' },
  },
];

// =========================================================================
// VISITOR — no chat/matching. Browse + carbon + soft upgrade CTA at the end.
// =========================================================================
const VISITOR_STEPS = [
  LANGUAGE_PICKER_STEP,
  {
    id: 'visitor-welcome',
    type: 'centered',
    titleKey: 'visitor.welcome.title',
    bodyKey: 'visitor.welcome.body',
    advance: { type: 'next_button' },
    ensureRoute: '/',
  },
  SHARED_OPEN_MENU,
  {
    id: 'visitor-companies',
    type: 'spotlight',
    titleKey: 'visitor.companies.title',
    bodyKey: 'visitor.companies.body',
    target: '[data-tutorial="nav-companies"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'visitor-events',
    type: 'spotlight',
    titleKey: 'visitor.events.title',
    bodyKey: 'visitor.events.body',
    target: '[data-tutorial="nav-events"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'visitor-carbon',
    type: 'spotlight',
    titleKey: 'visitor.carbon.title',
    bodyKey: 'visitor.carbon.body',
    target: '[data-tutorial="carbon-widget"]',
    placement: 'top',
    spotlightPadding: 6,
    // 🔥 [2026-06-30] 탄소 위젯이 없는 테넌트/화면에선 이 단계를 건너뜀
    condition: () => typeof document !== 'undefined' && !!document.querySelector('[data-tutorial="carbon-widget"]'),
    advance: { type: 'next_button' },
  },
  {
    id: 'visitor-profile',
    type: 'spotlight',
    titleKey: 'visitor.profile.title',
    bodyKey: 'visitor.profile.body',
    target: '[data-tutorial="nav-profile"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'visitor-upgrade',
    type: 'centered',
    titleKey: 'visitor.upgrade.title',
    bodyKey: 'visitor.upgrade.body',
    advance: { type: 'next_button' },
    cta: {
      labelKey: 'visitor.upgrade.cta',
      action: { type: 'navigate', path: '/profile?mode=upgrade' },
      finishAfter: true,
    },
  },
  {
    id: 'visitor-done',
    type: 'centered',
    titleKey: 'visitor.done.title',
    bodyKey: 'visitor.done.body',
    advance: { type: 'next_button' },
  },
];

// =========================================================================
// GUEST — preview only. End in a registration CTA.
// =========================================================================
const GUEST_STEPS = [
  LANGUAGE_PICKER_STEP,
  {
    id: 'guest-welcome',
    type: 'centered',
    titleKey: 'guest.welcome.title',
    bodyKey: 'guest.welcome.body',
    advance: { type: 'next_button' },
    ensureRoute: '/',
  },
  SHARED_OPEN_MENU,
  {
    id: 'guest-companies',
    type: 'spotlight',
    titleKey: 'guest.companies.title',
    bodyKey: 'guest.companies.body',
    target: '[data-tutorial="nav-companies"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'guest-events',
    type: 'spotlight',
    titleKey: 'guest.events.title',
    bodyKey: 'guest.events.body',
    target: '[data-tutorial="nav-events"]',
    placement: 'right',
    spotlightPadding: 6,
    advance: { type: 'next_button' },
  },
  {
    id: 'guest-register',
    type: 'centered',
    titleKey: 'guest.register.title',
    bodyKey: 'guest.register.body',
    advance: { type: 'next_button' },
    cta: {
      labelKey: 'guest.register.cta',
      action: { type: 'navigate', path: '/register' },
      finishAfter: true,
    },
  },
  {
    id: 'guest-done',
    type: 'centered',
    titleKey: 'guest.done.title',
    bodyKey: 'guest.done.body',
    advance: { type: 'next_button' },
  },
];

// =========================================================================
// resolveStepsForUser — pick the right step list based on user shape.
// Returns null when the user should NOT see any tutorial (admins, missing user).
// =========================================================================
export function resolveStepsForUser(user) {
  if (!user) return null;
  const t = String(user.user_type || '').toUpperCase();
  const verified = !!user.is_verified;

  if (t === 'ADMIN' || t === 'SUPER_ADMIN') return null;
  if (t === 'COMPANY') return buildMatchingTour('company');
  if (t === 'BUYER') {
    return verified ? buildMatchingTour('buyer') : buildPendingTour('buyerPending');
  }
  if (t === 'BUSINESS_VISITOR') {
    return verified
      ? buildMatchingTour('bizvisitor')
      : buildPendingTour('bizvisitorPending');
  }
  if (t === 'INVESTOR') return INVESTOR_STEPS;
  if (t === 'VISITOR') return VISITOR_STEPS;
  if (t === 'GUEST') return GUEST_STEPS;
  // Unknown user_type — fall back to visitor flow as a safe default.
  return VISITOR_STEPS;
}

export default { resolveStepsForUser };
